import React from "react";
import { useTranslation } from "react-i18next";
import { LobbyState } from "@uno/protocol";
import Tooltip from "component/Tooltip";
import useLocalStorage from "hook/useLocalStorage";
import AnimatedDots from "./AnimatedDots";

type StatusTitleProps = {
    countdown: number | null;
    lobbyState?: string;
    isPrivate?: boolean;
    isReady?: boolean;
};

const MAX_HINT_VIEWS = 3;
const TITLE_CLASS = "text-3xl sm:text-4xl lg:text-4xl viewport-landscape:text-[clamp(1.75rem,6vh,2.5rem)] viewport-landscape:leading-[1.1] text-center font-changa-one tracking-wide mb-2 md:mb-6 viewport-landscape:mb-[clamp(1.25rem,4vh,2rem)]";

const StatusTitle = ({ countdown, lobbyState, isPrivate, isReady }: StatusTitleProps) => {
    const { t } = useTranslation();
    const [showHint, setShowHint] = React.useState(false);
    const [hintViews, setHintViews] = useLocalStorage<number>("LASTDOTCARDS.BOT_HINT_VIEWS", 0);

    const hintCount = Number(hintViews) || 0;

    React.useEffect(() => {
        if(isPrivate || !isReady || countdown !== null || lobbyState === LobbyState.Launching || hintCount >= MAX_HINT_VIEWS) {
            setShowHint(false);
            return;
        }

        const timer = setTimeout(() => {
            setShowHint(true);
            setHintViews(String(hintCount + 1));
        }, 3000);
        return () => clearTimeout(timer);
    }, [isPrivate, isReady, countdown, lobbyState, hintCount, setHintViews]);

    if(countdown !== null && countdown > 0) {
        return (
            <h1 className={TITLE_CLASS}>
                <span className="gradient-text drop-shadow-[0_2px_4px_rgba(0,0,0,0.2)]">{t("lobby.game_starts_in")}</span>
                <span className="text-yellow-300 text-3xl lg:text-4xl drop-shadow-[0_0_8px_rgba(253,224,71,0.4)] tabular-nums">{Math.ceil(countdown)}</span>
                <span className="gradient-text drop-shadow-[0_2px_4px_rgba(0,0,0,0.2)]">{t("lobby.seconds")}</span>
            </h1>
        );
    }

    if(lobbyState === LobbyState.Launching) {
        return (
            <h1 className={TITLE_CLASS}>
                <span className="gradient-text drop-shadow-[0_2px_4px_rgba(0,0,0,0.2)]">{t("lobby.game_starting")}</span>
                <AnimatedDots />
            </h1>
        );
    }

    return (
        <Tooltip
            text={t("lobby.bot_fallback_hint")}
            visible={showHint}
            placement="bottom"
            duration={4000}
        >
            <h1 className={TITLE_CLASS}>
                <span className="gradient-text drop-shadow-[0_2px_4px_rgba(0,0,0,0.2)]">{t("lobby.searching_opponents")}</span>
                <AnimatedDots />
            </h1>
        </Tooltip>
    );
};

export default StatusTitle;
